import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Modal from '../ui/Modal';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setContentMasterQuotaModalVisible } from '../../store/slices/contentMasterSlice';

/**
 * "Not enough tokens" modal shown reactively when any Content Master API
 * (generate / translate / bulk-generate) is rejected with
 * `errorCode === 'content_master_quota_exceeded'`. The slice flips
 * `quotaExceededModalVisible` to `true` and deliberately does NOT set
 * `actionError`, so the regular error toast is suppressed and only this modal
 * is shown to the user.
 *
 * Mount this component once on every Content Master page that triggers
 * generation requests (Products list, Product detail, Bulk Review). It reads
 * its visibility flag from Redux so callers do not need to wire any props.
 */
const ContentMasterQuotaExceededModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const quotaExceededModalVisible = useAppSelector(
    (state) => state.contentMaster.quotaExceededModalVisible
  );

  return (
    <Modal
      isOpen={quotaExceededModalVisible}
      onClose={() => dispatch(setContentMasterQuotaModalVisible(false))}
      title={t('content_master_no_tokens_modal_title')}
      isCentered
      hideCloseButton
      className="content-master-confirm-modal content-master-confirm-modal--no-tokens"
    >
      <div className="content-master-confirm-modal__layout">
        <div className="content-master-confirm-modal__text">
          <p>{t('content_master_no_tokens_modal_body')}</p>
        </div>
        <div className="content-master-confirm-modal__illustration" aria-hidden />
      </div>
      <div className="content-master-confirm-modal__buttons">
        <button
          type="button"
          className="btn btn-secondary content-master-confirm-modal__btn-secondary"
          onClick={() => dispatch(setContentMasterQuotaModalVisible(false))}
        >
          {t('cancelText')}
        </button>
        <button
          type="button"
          className="btn btn-primary content-master-confirm-modal__btn-primary"
          onClick={() => {
            dispatch(setContentMasterQuotaModalVisible(false));
            navigate('/features/content-master/usage');
          }}
        >
          {t('content_master_no_tokens_modal_view_usage')}
          <svg
            className="content-master-confirm-modal__btn-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </Modal>
  );
};

export default ContentMasterQuotaExceededModal;
